import { useState } from 'react';
import { OTPInput } from '@/components/auth/OTPInput';
import { supabase } from '@/lib/supabase';

interface FormData {
  email: string;
  phone: string;
  name: string;
}

interface PasswordData {
  password: string;
  confirmPassword: string;
}

interface SellerAccountFormProps {
  formData: FormData;
  passwordData: PasswordData;
  onFormChange: (field: keyof FormData, value: string) => void;
  onPasswordChange: (field: keyof PasswordData, value: string) => void;
  isEmailVerified: boolean;
  onEmailVerified: (verified: boolean, token?: string) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
  className?: string;
}

export function SellerAccountForm({ 
  formData, 
  passwordData,
  onFormChange,
  onPasswordChange,
  isEmailVerified,
  onEmailVerified,
  onError,
  onSuccess,
  className = '' 
}: SellerAccountFormProps) {
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isOTPLoading, setIsOTPLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | undefined>();
  const [_nextAllowedAt, setNextAllowedAt] = useState<string | undefined>();

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === 'phone') {
      value = value.replace(/[^0-9]/g, '');
    }
    onFormChange(field, value);
  };

  const handlePasswordChange = (field: keyof PasswordData, value: string) => {
    onPasswordChange(field, value);
  };

  // OTP 발송
  const handleSendOTP = async () => {
    if (!formData.email) {
      onError('이메일을 입력해주세요');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      onError('올바른 이메일 형식을 입력해주세요');
      return;
    }

    if (!passwordData.password || passwordData.password.length < 8) {
      onError('비밀번호를 8자리 이상 입력해주세요');
      return;
    }

    setIsResending(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('send-otp', {
        body: { 
          email: formData.email,
          purpose: 'signup',
          name: formData.name
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setShowOTPInput(true);
        setRemainingAttempts(data.remainingAttempts);
        onSuccess(data.message);
      } else {
        setOtpError(data?.error || '인증번호 발송에 실패했습니다.');
        setRemainingAttempts(data?.remainingAttempts);
        setNextAllowedAt(data?.nextAllowedAt);
      }
    } catch (error: any) {
      console.error('OTP 발송 오류:', error);
      setOtpError(error.message || '인증번호 발송 중 오류가 발생했습니다.');
    } finally {
      setIsResending(false);
    }
  };

  // OTP 검증
  const handleVerifyOTP = async (otp: string) => {
    setIsOTPLoading(true);
    setOtpError('');

    try {
      const { data, error } = await supabase.functions.invoke('verify-otp', {
        body: { 
          email: formData.email,
          otp,
          purpose: 'signup'
        }
      });

      if (error) {
        throw error;
      }

      if (data?.success) {
        setShowOTPInput(false);
        onEmailVerified(true, data.verificationToken);
        onSuccess('이메일 인증이 완료되었습니다!');
      } else {
        setOtpError(data?.error || '인증번호가 올바르지 않습니다.');
        setRemainingAttempts(data?.remainingAttempts);
      }
    } catch (error: any) {
      console.error('OTP 검증 오류:', error);
      setOtpError(error.message || '인증번호 확인 중 오류가 발생했습니다.');
    } finally {
      setIsOTPLoading(false);
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* 이메일 입력 및 인증 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          이메일
        </label>
        <div className="flex gap-4">
          <input
            type="email"
            name="email"
            id="email"
            autoComplete="username"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="이메일을 입력해주세요"
            className="flex-1 h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
          />
          {!isEmailVerified && (
            <button
              type="button"
              onClick={handleSendOTP}
              disabled={isResending}
              className={`px-4 py-2 font-medium text-[14px] rounded-lg transition-all whitespace-nowrap ${
                !isResending
                  ? 'bg-[#4CD964] text-white hover:bg-[#45B85C] active:scale-95'
                  : 'bg-[#DEDEDE] text-[#222222] cursor-not-allowed'
              }`}
            >
              {isResending ? '인증중...' : '인증하기'}
            </button>
          )}
          
          {isEmailVerified && (
            <div className="flex items-center px-4">
              <span className="text-[#4CD964] text-[16px] font-medium">✓ 인증완료</span>
            </div>
          )}
        </div>
      </div>

      {/* OTP 입력 */}
      {showOTPInput && !isEmailVerified && (
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="text-[16px] text-gray-600 mb-4 text-center">
            <strong>{formData.email}</strong>로 발송된 인증번호를 입력해주세요
          </div>
          
          <OTPInput
            onComplete={handleVerifyOTP}
            onResend={handleSendOTP}
            isLoading={isOTPLoading}
            isResending={isResending}
            error={otpError}
            remainingAttempts={remainingAttempts}
          />
        </div>
      )}

      {/* 비밀번호 입력 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          비밀번호(8자리)
        </label>
        <input
          type="password"
          name="password"
          id="new-password"
          autoComplete="new-password"
          value={passwordData.password}
          onChange={(e) => handlePasswordChange('password', e.target.value)}
          placeholder="비밀번호 8자리를 입력해주세요"
          className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
        />
      </div>

      {/* 비밀번호 재확인 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          비밀번호 재확인
        </label>
        <input
          type="password"
          name="confirm-password"
          id="confirm-password"
          autoComplete="new-password"
          value={passwordData.confirmPassword}
          onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
          placeholder="비밀번호를 다시 입력해주세요"
          className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
        />
      </div>

      {/* 전화번호 입력 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          전화번호(번호만 입력)
        </label>
        <input
          type="tel"
          name="phone"
          id="phone"
          autoComplete="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange('phone', e.target.value)}
          placeholder="예시) 01011111111"
          className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
        />
      </div>

      {/* 이름 입력 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          이름
        </label>
        <input
          type="text"
          name="name"
          id="name"
          autoComplete="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          placeholder="이름을 입력해주세요"
          className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#51C854] placeholder:text-[#606060]"
        />
      </div>
    </div>
  );
}